import { cva, type VariantProps } from 'class-variance-authority'
import React, { ElementType, HTMLAttributes, forwardRef } from 'react'
import { cn } from './cn'

export const headingVariants = cva('m-0 font-semibold', {
  variants: {
    level: {
      display: 'font-display text-[40px] leading-[1.05] tracking-[-0.025em] sm:text-[56px]',
      1: 'font-display text-[32px] leading-[1.1] tracking-[-0.02em] sm:text-[40px]',
      2: 'font-display text-[28px] leading-[1.15] tracking-[-0.015em] sm:text-[32px]',
      3: 'font-display text-[24px] leading-[1.25] tracking-[-0.01em]',
      4: 'font-text text-[20px] leading-[1.3] tracking-[-0.005em]',
      5: 'font-text text-[17px] leading-[1.35]',
    },
    tone: {
      inherit: '',
      fg: 'text-fg',
      'fg-2': 'text-fg-2',
      success: 'text-success',
      danger: 'text-danger',
      warning: 'text-warning',
      info: 'text-info',
    },
    align: {
      start: 'text-left',
      center: 'text-center',
      end: 'text-right',
    },
    tabular: { true: 'tabular' },
    truncate: { true: 'truncate' },
  },
  defaultVariants: { level: 3, tone: 'inherit' },
})

const TAGS: Record<string, ElementType> = {
  display: 'h1',
  1: 'h1',
  2: 'h2',
  3: 'h3',
  4: 'h4',
  5: 'h5',
}

export interface HeadingProps
  extends Omit<HTMLAttributes<HTMLHeadingElement>, 'color'>,
    VariantProps<typeof headingVariants> {
  as?: ElementType
}

/** Titles and balances. Levels display–3 are Sora; 4–5 are Inter. */
export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ as, level = 3, tone, align, tabular, truncate, className, ...props }, ref) => {
    const Comp = as ?? TAGS[String(level)] ?? 'h3'
    return (
      <Comp
        ref={ref}
        className={cn(headingVariants({ level, tone, align, tabular, truncate }), className)}
        {...props}
      />
    )
  }
)
Heading.displayName = 'Heading'
